import { useMutation, useQuery } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import { RESOURCE_QUERY_META } from "@/api/resourceQuery";

import { collectionsApi } from "./collectionsApi";
import type {
  CollectionCreateRequest,
  CollectionPreviewRequest,
  CollectionsSummaryList,
  CollectionUpdateRequest,
  ICollection,
  ICollectionOrderList,
  SavedCollectionPreviewRequest,
} from "./types";

export const usePreviewCollectionMutation = () => {
  return useMutation({
    mutationFn: (data: CollectionPreviewRequest) =>
      collectionsApi.previewCollection(data),
  });
};

export const usePreviewSavedCollectionMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: SavedCollectionPreviewRequest;
    }) => collectionsApi.previewSavedCollection(id, data),
  });
};

export const useCreateCollectionMutation = () => {
  return useMutation({
    mutationFn: (data: CollectionCreateRequest) => {
      return collectionsApi.postNewCollection(data);
    },
  });
};

export const useDeleteCollectionMutation = () => {
  return useMutation({
    mutationFn: (id: string) => {
      return collectionsApi.deleteCollection(id);
    },
  });
};

export const useUpdateCollectionMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: CollectionUpdateRequest;
    }) => {
      return collectionsApi.patchCollection(id, data);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.collections, id] });
    },
  });
};

export const useGetAllCollectionsQuery = () => {
  return useQuery<CollectionsSummaryList>({
    queryKey: [QUERY_KEYS.collections],
    queryFn: () => collectionsApi.getAllCollections(),
  });
};

export const useGetCollectionByIdQuery = (id: string) => {
  return useQuery<ICollection>({
    queryKey: [QUERY_KEYS.collections, id],
    queryFn: () => collectionsApi.getCollectionById(id),
    enabled: !!id,
    meta: RESOURCE_QUERY_META,
  });
};

export const useDeactivateCollectionMutation = () => {
  return useMutation({
    mutationFn: (id: string) => {
      return collectionsApi.deactivateCollection(id);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.collections, id] });
    },
  });
};

export const useUpdateCollectionOrderMutation = () => {
  return useMutation({
    mutationFn: (data: ICollectionOrderList) => {
      return collectionsApi.postCollectionOrder(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.collections],
      });
    },
  });
};
